import { getDecodedToken, getEncodedTokenV4, type Proof, type Token } from '@cashu/cashu-ts';

/** Get total amount for a set of proofs */
export function getAmountForTokenSet(proofs: Proof[]): number {
  return proofs.reduce((acc, p) => acc + p.amount, 0);
}

/** Format a Cashu amount with currency symbol */
export function formatAmount(amount: number, unit?: string): string {
  if (!unit) unit = 'sat';
  if (unit === 'sat') return `${new Intl.NumberFormat('en-US').format(amount)} sats`;
  if (unit === 'msat') {
    return `${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(amount / 1000)} sats`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    currency: unit.toUpperCase(),
  }).format(amount / 100);
}

/** Get unit symbol */
export function getUnitSymbol(unit: string): string {
  switch (unit) {
    case 'sat': return '\u20BF';
    case 'msat': return '\u20BF';
    case 'btc': return '\u20BF';
    case 'usd': return '$';
    case 'eur': return '\u20AC';
    case 'gbp': return '\u00A3';
    case 'jpy': return '\u00A5';
    default: return unit;
  }
}

/** Try to decode a Cashu token string and return basic info */
export function decodeCashuToken(tokenStr: string): {
  token: Token;
  amount: number;
  mint: string;
  unit: string;
  encoded: string;
} | null {
  try {
    const token = getDecodedToken(tokenStr);
    const amount = getAmountForTokenSet(token.proofs);
    return {
      token,
      amount,
      mint: token.mint,
      unit: token.unit ?? 'sat',
      encoded: getEncodedTokenV4(token),
    };
  } catch {
    return null;
  }
}

/** Validate that a string is a Cashu token */
export function isCashuToken(str: string): boolean {
  return str.startsWith('cashuA') || str.startsWith('cashuB');
}

/** Shorten a mint URL for display */
export function shortenMintUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.hostname;
  } catch {
    return url.length > 30 ? url.slice(0, 30) + '...' : url;
  }
}

// Re-export types for convenience
export type { Token, Proof };
